import { useEffect, useState } from "react";
import BackofficeLayout, { Card, KpiCard, fmtEUR } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { TrendingUp, TrendingDown, AlertTriangle, ArrowUpRight, ArrowDownRight, Building2, Edit3, Plus, Trash2, Save, X } from "lucide-react";

export default function TreasuryView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [filterEntity, setFilterEntity] = useState("");
  const [projectionMonths, setProjectionMonths] = useState(12);
  const [snapshots, setSnapshots] = useState([]);
  const [editingSnapshot, setEditingSnapshot] = useState(null); // {month, balance, note, includes_month_flows}

  useEffect(() => {
    api.get("/backoffice/entities").then((r) => setEntities(r.data));
  }, []);

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams({
      history_months: "12",
      projection_months: String(projectionMonths),
    });
    if (filterEntity) params.append("entity_id", filterEntity);
    return Promise.all([
      api.get(`/backoffice/treasury/detail?${params.toString()}`).then((r) => setData(r.data)),
      api.get(`/backoffice/treasury/snapshots${filterEntity ? `?entity_id=${filterEntity}` : ""}`).then((r) => setSnapshots(r.data || [])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [filterEntity, projectionMonths]);

  const saveSnapshot = async () => {
    if (!editingSnapshot?.month) return toast.error("Mois requis (format YYYY-MM)");
    try {
      await api.post("/backoffice/treasury/snapshots", {
        month: editingSnapshot.month,
        balance: parseFloat(editingSnapshot.balance) || 0,
        note: editingSnapshot.note || null,
        includes_month_flows: editingSnapshot.includes_month_flows !== false,
        entity_id: filterEntity || null,
      });
      toast.success("Solde manuel enregistré");
      setEditingSnapshot(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const deleteSnapshot = async (month) => {
    if (!confirm(`Supprimer le solde manuel de ${month} ?`)) return;
    try {
      const url = `/backoffice/treasury/snapshots/${month}${filterEntity ? `?entity_id=${filterEntity}` : ""}`;
      await api.delete(url);
      toast.success("Solde manuel supprimé");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  if (loading || !data) {
    return <BackofficeLayout title="Trésorerie"><div className="text-slate-400" data-testid="bof-treasury-loading">Chargement…</div></BackofficeLayout>;
  }

  const { current_total, cash_flow_previsionnel, breakdown, accounts, history_totals, projection, alerts, nb_accounts, current_month_ym, current_month_ref_day } = data;
  const b = breakdown;
  const totalRevenus = (b.revenus_recurrents || 0) + (b.revenus_baux || 0);
  const totalCharges = (b.charges_recurrentes || 0) + (b.charges_baux_tf || 0) + (b.mensualites_prets || 0);
  const revenusCurrent = (b.revenus_recurrents_current ?? b.revenus_recurrents) + (b.revenus_baux_current ?? b.revenus_baux);

  // Combined chart data: history + projection with loan events
  const chartData = [
    ...history_totals.map((h) => ({ month: h.month, balance: h.balance, type: "history", manual: !!h.manual, loan_events: [], flow: null })),
    ...projection.map((p) => ({
      month: p.month, balance: p.balance, type: "projection",
      loan_events: p.loan_events || [], flow: p.flow,
      revenue: p.revenue, charges_recurrentes: p.charges_recurrentes,
      loans_amount: p.loans_amount,
      manual: !!p.manual,
      vacancy_impacts: p.vacancy_impacts || [],
      vacancy_total: p.vacancy_total || 0,
    })),
  ];
  const maxBal = Math.max(...chartData.map((d) => d.balance), current_total, 1);
  const minBal = Math.min(...chartData.map((d) => d.balance), 0);
  const range = maxBal - minBal || 1;

  return (
    <BackofficeLayout
      title="Trésorerie"
      subtitle="Solde temps réel, historique 12 mois et projection paramétrable basée sur cash-flow, échéances de baux et prêts."
      actions={
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded p-1 flex-wrap" data-testid="bof-treasury-horizon">
            {[3, 6, 12, 24, 36].map((m) => (
              <button key={m} onClick={() => setProjectionMonths(m)}
                      className={`text-xs px-2.5 py-1 rounded font-semibold transition-colors ${
                        projectionMonths === m
                          ? "bg-amber-400 text-slate-950"
                          : "text-slate-400 hover:text-white"
                      }`}
                      data-testid={`bof-treasury-horizon-${m}`}>
                {m}M
              </button>
            ))}
          </div>
          <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white min-w-0 max-w-full flex-1 sm:flex-none"
                  data-testid="bof-treasury-entity-filter">
            <option value="">Toutes les entités</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      }
    >
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="bof-treasury-alerts">
          {alerts.map((a, i) => (
            <div key={i}
                 className={`p-3 rounded border flex items-start gap-3 text-sm ${
                   a.level === "critical"
                     ? "bg-red-500/10 border-red-800 text-red-200"
                     : "bg-amber-500/10 border-amber-700 text-amber-200"
                 }`}
                 data-testid={`bof-treasury-alert-${i}`}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Info-bar : mois courant + date de référence */}
      <Toaster theme="dark" position="top-right" />
      <div className="mb-6 p-3 rounded border border-slate-700 bg-slate-900/60 text-xs text-slate-400 flex flex-wrap items-center gap-4"
           data-testid="bof-treasury-current-month-info">
        <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Mois courant : {current_month_ym}</span>
        <span>Date de référence solde : <span className="text-white font-bold">jour {current_month_ref_day}</span></span>
        <span className="text-slate-500">→ Loyers / prêts / récurrents avec jour ≤ {current_month_ref_day} sont considérés déjà encaissés/payés (non ajoutés à ce mois)</span>
        <span className="ml-auto">Revenus restants ce mois : <span className="text-emerald-300 font-bold">{fmtEUR(revenusCurrent)}</span> / plein mois {fmtEUR(totalRevenus)}</span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="bof-treasury-kpis">
        <KpiCard label="Trésorerie totale" value={fmtEUR(current_total)}
                 sub={`${nb_accounts} comptes`}
                 tone={current_total >= 0 ? "amber" : "red"}
                 testid="kpi-treso-total" />
        <KpiCard label="Cash-flow prévisionnel"
                 value={fmtEUR(cash_flow_previsionnel)}
                 sub="Par mois (baux + récurrents − prêts)"
                 tone={cash_flow_previsionnel >= 0 ? "green" : "red"}
                 testid="kpi-cf-previsionnel" />
        <KpiCard label={`Trésorerie dans ${projectionMonths} mois`}
                 value={fmtEUR(projection[projection.length - 1]?.balance || 0)}
                 sub={`Écart ${(projection[projection.length - 1]?.balance - current_total) >= 0 ? "+" : ""}${fmtEUR((projection[projection.length - 1]?.balance || 0) - current_total)}`}
                 tone={projection[projection.length - 1]?.balance >= 0 ? "blue" : "red"}
                 testid="kpi-treso-projected" />
        <KpiCard label="Runway (mois)"
                 value={cash_flow_previsionnel < 0 && current_total > 0
                   ? Math.floor(current_total / Math.abs(cash_flow_previsionnel))
                   : "∞"}
                 sub={cash_flow_previsionnel < 0 ? "Avant trésorerie à 0" : "Cash-flow positif"}
                 tone={cash_flow_previsionnel >= 0 ? "green" : "red"}
                 testid="kpi-runway" />
      </div>

      {/* Chart historique + projection */}
      <Card className="mb-8" data-testid="bof-treasury-chart-card">
        <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Évolution de la trésorerie</div>
            <div className="text-sm text-slate-300 mt-1">12 mois passés (historique) + {projectionMonths} mois futurs (projection)</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-400 rounded-sm" /> Historique</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-sky-400/60 rounded-sm border border-sky-400 border-dashed" /> Projection</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-400 rounded-full" /> Échéance prêt</span>
          </div>
        </div>
        <div className="md:hidden text-[10px] text-slate-500 italic mb-2 flex items-center gap-1.5">
          <span>← Glissez horizontalement pour voir tous les mois →</span>
        </div>
        <TreasuryChart data={chartData} maxBal={maxBal} minBal={minBal} range={range} />
      </Card>

      {/* Soldes manuels par mois (snapshots) */}
      <Card className="mb-8" data-testid="bof-treasury-snapshots">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Soldes manuels par mois
            </div>
            <div className="text-xs text-slate-500 mt-1 max-w-2xl">
              Force la valeur du solde de trésorerie pour un mois précis (ancre la projection).
              Utile pour aligner le graphique avec ta banque, ou pour dire « en juillet j&apos;ai déjà encaissé mes loyers ».
            </div>
          </div>
          <button onClick={() => setEditingSnapshot({ month: current_month_ym, balance: current_total, note: "", includes_month_flows: true })}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition"
                  data-testid="bof-treasury-snapshot-add-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter un solde
          </button>
        </div>

        {snapshots.length === 0 ? (
          <div className="text-xs text-slate-500 italic border border-dashed border-slate-800 rounded p-4 text-center">
            Aucun solde manuel. Par défaut, l&apos;évolution est calculée automatiquement depuis les transactions.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-4">Mois</th>
                  <th className="py-2 pr-4 text-right">Solde (€)</th>
                  <th className="py-2 pr-4 text-center">Revenus/charges du mois inclus ?</th>
                  <th className="py-2 pr-4">Note</th>
                  <th className="py-2 pr-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.month + (s.entity_id || "")} className="border-b border-slate-800/50" data-testid={`bof-snapshot-row-${s.month}`}>
                    <td className="py-2 pr-4 font-mono font-bold text-amber-300">{s.month}</td>
                    <td className="py-2 pr-4 text-right font-mono font-bold text-white">{fmtEUR(s.balance)}</td>
                    <td className="py-2 pr-4 text-center text-xs">
                      {s.includes_month_flows !== false
                        ? <span className="text-emerald-300">✓ Oui (revenus/charges déjà dedans)</span>
                        : <span className="text-slate-400">Non (ancrage pur)</span>}
                    </td>
                    <td className="py-2 pr-4 text-xs text-slate-400">{s.note || "—"}</td>
                    <td className="py-2 pr-2 text-right">
                      <button onClick={() => setEditingSnapshot({ ...s })}
                              className="text-amber-300 hover:text-amber-100 mr-2"
                              data-testid={`bof-snapshot-edit-${s.month}`}>
                        <Edit3 className="w-3.5 h-3.5 inline" />
                      </button>
                      <button onClick={() => deleteSnapshot(s.month)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`bof-snapshot-delete-${s.month}`}>
                        <Trash2 className="w-3.5 h-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal d'édition de snapshot */}
      {editingSnapshot && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10"
             onClick={(e) => { if (e.target === e.currentTarget) setEditingSnapshot(null); }}
             data-testid="bof-snapshot-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg shadow-2xl">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div className="text-sm font-black uppercase tracking-widest text-white">
                Solde manuel — {editingSnapshot.month || "nouveau"}
              </div>
              <button onClick={() => setEditingSnapshot(null)} className="text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Mois (YYYY-MM)</span>
                <input type="month" value={editingSnapshot.month || ""}
                       onChange={(e) => setEditingSnapshot({ ...editingSnapshot, month: e.target.value })}
                       className="input" data-testid="bof-snapshot-month-input" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Solde total du groupe (€)</span>
                <input type="number" step="0.01" value={editingSnapshot.balance ?? ""}
                       onChange={(e) => setEditingSnapshot({ ...editingSnapshot, balance: e.target.value })}
                       className="input" data-testid="bof-snapshot-balance-input" />
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 border border-slate-800 rounded hover:bg-slate-800/40">
                <input type="checkbox"
                       checked={editingSnapshot.includes_month_flows !== false}
                       onChange={(e) => setEditingSnapshot({ ...editingSnapshot, includes_month_flows: e.target.checked })}
                       className="mt-1"
                       data-testid="bof-snapshot-includes-flows" />
                <div className="text-xs text-slate-300 leading-relaxed">
                  <div className="font-bold text-white">Le solde saisi inclut déjà les revenus/charges du mois</div>
                  <div className="text-slate-500 mt-1">
                    ✓ Coché : les loyers et charges récurrents ne sont PAS ré-ajoutés (seuls les prêts à venir s&apos;appliquent).<br />
                    ✗ Décoché : ancrage pur — tous les flux normaux du mois s&apos;ajoutent par-dessus.
                  </div>
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">Note (optionnel)</span>
                <input type="text" value={editingSnapshot.note || ""}
                       onChange={(e) => setEditingSnapshot({ ...editingSnapshot, note: e.target.value })}
                       placeholder="Ex: Solde bancaire au 3/07 après encaissement loyers"
                       className="input" data-testid="bof-snapshot-note-input" />
              </label>
            </div>
            <div className="p-5 border-t border-slate-700 flex items-center justify-end gap-2">
              <button onClick={() => setEditingSnapshot(null)}
                      className="px-3 py-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white">
                Annuler
              </button>
              <button onClick={saveSnapshot}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 rounded"
                      data-testid="bof-snapshot-save-btn">
                <Save className="w-3.5 h-3.5" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impacts vacance / impayés */}
      {projection.some((p) => (p.vacancy_impacts || []).length > 0) && (
        <Card className="mb-8" data-testid="bof-treasury-vacancy">
          <div className="text-[10px] uppercase tracking-widest text-red-400 font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Loyers non perçus (vacance & impayés) — impact projection
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-4">Mois</th>
                  <th className="py-2 pr-4">Bail impacté</th>
                  <th className="py-2 pr-4">Raison</th>
                  <th className="py-2 pr-4 text-right">Perte mois</th>
                  <th className="py-2 pr-4">Note</th>
                </tr>
              </thead>
              <tbody>
                {projection.filter((p) => (p.vacancy_impacts || []).length > 0).flatMap((p) =>
                  p.vacancy_impacts.map((v, i) => (
                    <tr key={`${p.month}-${i}`} className="border-b border-slate-800/50">
                      <td className="py-2 pr-4 font-mono font-bold text-red-300">{p.month}</td>
                      <td className="py-2 pr-4 text-white">{v.tenant_name || "—"}</td>
                      <td className="py-2 pr-4">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                          v.reason === "unpaid" ? "bg-red-500/15 border-red-500/40 text-red-300"
                          : v.reason === "free_month" ? "bg-sky-500/15 border-sky-500/40 text-sky-300"
                          : v.reason === "carence" ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
                          : "bg-amber-500/15 border-amber-500/40 text-amber-300"
                        }`}>
                          {v.reason === "unpaid" ? "Impayé" : v.reason === "free_month" ? "Franchise" : v.reason === "carence" ? "Carence" : "Vacant"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono font-bold text-red-300">−{fmtEUR(v.amount)}</td>
                      <td className="py-2 pr-4 text-xs text-slate-400">{v.note || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-red-500/30 bg-red-500/5">
                  <td colSpan="3" className="py-2 pr-4 text-right text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total perte projection</td>
                  <td className="py-2 pr-4 text-right font-mono font-bold text-red-300">
                    −{fmtEUR(projection.reduce((s, p) => s + (p.vacancy_total || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Timeline des échéances de prêts */}
      {projection.some((p) => (p.loan_events || []).length > 0) && (
        <Card className="mb-8" data-testid="bof-treasury-loan-timeline">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
            Prochaines échéances de prêts sur les {projectionMonths} mois
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-4">Mois</th>
                  <th className="py-2 pr-4">Prêteur(s)</th>
                  <th className="py-2 pr-4 text-right">Montant du mois</th>
                  <th className="py-2 pr-4 text-right">Flux net</th>
                  <th className="py-2 pr-4 text-right">Solde après</th>
                </tr>
              </thead>
              <tbody>
                {projection.filter((p) => (p.loan_events || []).length > 0).map((p) => (
                  <tr key={p.month} className="border-b border-slate-800/50">
                    <td className="py-2 pr-4 font-mono font-bold text-amber-300">{p.month}</td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {p.loan_events.map((ev, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-200">
                            {ev.lender} : {fmtEUR(ev.amount)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-red-300">−{fmtEUR(p.loans_amount)}</td>
                    <td className={`py-2 pr-4 text-right font-mono font-bold ${p.flow >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {p.flow >= 0 ? "+" : ""}{fmtEUR(p.flow)}
                    </td>
                    <td className={`py-2 pr-4 text-right font-mono font-bold ${p.balance >= 0 ? "text-white" : "text-red-300"}`}>
                      {fmtEUR(p.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Breakdown cash-flow prévisionnel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Card data-testid="bof-treasury-breakdown-revenus">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-2">
            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" /> Revenus mensuels prévisionnels
          </div>
          <BreakdownRow label="Baux locatifs (loyer + charges + TF refac.)" value={b.revenus_baux} tone="emerald" />
          <BreakdownRow label="Récurrents (loyers, autres)" value={b.revenus_recurrents} tone="emerald" />
          <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between items-baseline">
            <span className="font-bold text-white">Total</span>
            <span className="text-xl font-black text-emerald-300">{fmtEUR(totalRevenus)}</span>
          </div>
        </Card>
        <Card data-testid="bof-treasury-breakdown-charges">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-2">
            <ArrowDownRight className="w-3.5 h-3.5 text-red-400" /> Charges mensuelles prévisionnelles
          </div>
          <BreakdownRow label="Taxe foncière mensualisée (baux)" value={b.charges_baux_tf} tone="red" />
          <BreakdownRow label="Charges récurrentes" value={b.charges_recurrentes} tone="red" />
          <BreakdownRow label="Mensualités prêts bancaires" value={b.mensualites_prets} tone="red" />
          <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between items-baseline">
            <span className="font-bold text-white">Total</span>
            <span className="text-xl font-black text-red-300">{fmtEUR(totalCharges)}</span>
          </div>
        </Card>
      </div>

      {/* Table par compte */}
      <Card data-testid="bof-treasury-accounts-table">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Détail par compte</div>
        {accounts.length === 0 ? (
          <div className="text-slate-500 text-sm py-6 text-center">Aucun compte bancaire. Créez-en un dans <em>Comptes bancaires</em>.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr className="border-b border-slate-800">
                  <th className="py-2 pr-4">Compte</th>
                  <th className="py-2 pr-4">Entité</th>
                  <th className="py-2 pr-4 text-right">Ouverture</th>
                  <th className="py-2 pr-4 text-right">Solde actuel</th>
                  <th className="py-2 pr-4 text-right">Trend 3 mois</th>
                  <th className="py-2 pr-4">Mini-graph 12 mois</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc) => (
                  <tr key={acc.account_id} className="border-b border-slate-800/50" data-testid={`bof-treasury-account-${acc.account_id}`}>
                    <td className="py-3 pr-4 font-bold text-white flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded" style={{ background: acc.entity_color }} />
                      {acc.account_name}
                    </td>
                    <td className="py-3 pr-4 text-xs text-slate-400 flex items-center gap-1.5">
                      <Building2 className="w-3 h-3" /> {acc.entity_name}
                    </td>
                    <td className="py-3 pr-4 text-right font-mono text-slate-400">{fmtEUR(acc.opening_balance)}</td>
                    <td className={`py-3 pr-4 text-right font-mono font-bold ${acc.current_balance >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {fmtEUR(acc.current_balance)}
                      {acc.balance_date && (
                        <div className="text-[10px] font-normal text-amber-400 mt-0.5">au {acc.balance_date}</div>
                      )}
                    </td>
                    <td className={`py-3 pr-4 text-right font-mono text-xs ${(acc.trend_3m || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {acc.trend_3m == null ? "—" : (
                        <span className="inline-flex items-center gap-1">
                          {acc.trend_3m >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {acc.trend_3m >= 0 ? "+" : ""}{fmtEUR(acc.trend_3m)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4"><MiniSparkline history={acc.history} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-700">
                  <td colSpan={3} className="py-3 pr-4 font-bold text-white">Total</td>
                  <td className={`py-3 pr-4 text-right font-mono font-black text-lg ${current_total >= 0 ? "text-amber-300" : "text-red-300"}`}>
                    {fmtEUR(current_total)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </BackofficeLayout>
  );
}

/* ---------- Chart : historique (barres pleines) + projection (barres dashed) + loan events markers ---------- */
const TreasuryChart = ({ data, maxBal, minBal, range }) => {
  const [selected, setSelected] = useState(null); // month string (mobile tap)
  if (!data.length) return null;
  const barHeight = (v) => {
    const positive = Math.max(0, v);
    return `${(positive / range) * 100}%`;
  };
  const zeroLinePos = ((0 - minBal) / range) * 100;
  const selectedItem = selected ? data.find((d) => d.month === selected) : null;

  return (
    <div data-testid="bof-treasury-chart-wrapper">
      {/* Scrollable chart container : chaque barre garde une largeur mini de 28px sur mobile */}
      <div className="overflow-x-auto -mx-1 pb-1" data-testid="bof-treasury-chart-scroll">
        <div className="relative h-64 pt-6" style={{ minWidth: `${Math.max(data.length * 28, 300)}px` }} data-testid="bof-treasury-chart">
          {/* Zero line if minBal < 0 */}
          {minBal < 0 && (
            <div className="absolute left-0 right-0 border-t border-slate-600 border-dashed z-10 flex justify-end pr-2"
                 style={{ bottom: `${zeroLinePos}%` }}>
              <span className="text-[9px] text-slate-500 -mt-3">0 €</span>
            </div>
          )}
          <div className="grid gap-1 h-full items-end" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))` }}>
            {data.map((d) => {
              const isProjection = d.type === "projection";
              const isNegative = d.balance < 0;
              const hasLoanEvent = (d.loan_events || []).length > 0;
              const hasVacancy = (d.vacancy_impacts || []).length > 0;
              const vacancyTotal = d.vacancy_total || 0;
              const loansTotal = (d.loan_events || []).reduce((s, ev) => s + ev.amount, 0);
              const isSelected = selected === d.month;
              return (
                <div key={d.month}
                     className="flex flex-col items-center h-full justify-end group relative cursor-pointer"
                     onClick={() => setSelected(isSelected ? null : d.month)}
                     data-testid={`bof-chart-bar-${d.month}`}>
                  <div
                    className={`w-full rounded-t transition-all relative ${
                      isProjection
                        ? "bg-sky-400/40 border border-sky-400 border-dashed"
                        : isNegative ? "bg-red-500/70" : "bg-amber-400/80"
                    } ${isSelected ? "ring-2 ring-white brightness-125" : "group-hover:brightness-125"}`}
                    style={{ height: barHeight(d.balance) }}
                  >
                    {hasLoanEvent && (
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-red-400 rounded-full border-2 border-slate-900"
                           title={`Échéance prêt: ${loansTotal.toLocaleString("fr-FR")} €`} />
                    )}
                    {hasVacancy && (
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] leading-none px-1 py-0.5 rounded bg-red-500/90 text-white font-bold border border-red-700 shadow-md"
                           title={`Loyer non perçu: −${vacancyTotal.toLocaleString("fr-FR")} €`}
                           data-testid={`bof-chart-vacancy-warn-${d.month}`}>
                        ⚠
                      </div>
                    )}
                  </div>
                  <div className={`text-[9px] font-mono mt-1 whitespace-nowrap ${hasVacancy ? "text-red-300 font-bold" : "text-slate-500"} ${isSelected ? "text-amber-300 font-bold" : ""}`}>{d.month.slice(2).replace("-", "/")}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Panneau détail — s'affiche sur clic/tap d'une barre (mobile-first, aussi utile sur desktop) */}
      {selectedItem ? (
        <ChartDetailPanel item={selectedItem} onClose={() => setSelected(null)} />
      ) : (
        <div className="mt-3 text-[10px] text-slate-500 italic text-center" data-testid="bof-chart-tap-hint">
          Cliquez sur une barre pour voir le détail du mois
        </div>
      )}
    </div>
  );
};

const ChartDetailPanel = ({ item, onClose }) => {
  const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
  const isProjection = item.type === "projection";
  const hasLoanEvent = (item.loan_events || []).length > 0;
  const hasVacancy = (item.vacancy_impacts || []).length > 0;
  return (
    <div className="mt-4 p-4 border border-amber-500/30 bg-amber-500/5 rounded" data-testid={`bof-chart-detail-${item.month}`}>
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Détail du mois</div>
          <div className="text-lg font-bold text-white mt-0.5 font-mono">{item.month}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            {isProjection ? "Projection" : "Historique"}
            {item.manual && " · Solde manuel"}
          </div>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white text-lg leading-none px-2 shrink-0"
                data-testid="bof-chart-detail-close">×</button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="col-span-2 flex justify-between items-baseline py-1.5 border-b border-slate-800">
          <span className="text-slate-400">Solde</span>
          <span className={`font-mono font-black text-base ${item.balance >= 0 ? "text-white" : "text-red-300"}`}>{fmt(item.balance)}</span>
        </div>
        {isProjection && (
          <>
            <div className="flex justify-between items-baseline py-1">
              <span className="text-slate-400">+ Revenus</span>
              <span className="font-mono font-bold text-emerald-300">{fmt(item.revenue)}</span>
            </div>
            <div className="flex justify-between items-baseline py-1">
              <span className="text-slate-400">− Charges</span>
              <span className="font-mono font-bold text-red-300">{fmt(item.charges_recurrentes)}</span>
            </div>
            <div className="flex justify-between items-baseline py-1">
              <span className="text-slate-400">− Prêts</span>
              <span className="font-mono font-bold text-red-300">{fmt(item.loans_amount)}</span>
            </div>
            <div className="flex justify-between items-baseline py-1">
              <span className="text-slate-400">Flux net</span>
              <span className={`font-mono font-bold ${(item.flow || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {(item.flow || 0) >= 0 ? "+" : ""}{fmt(item.flow)}
              </span>
            </div>
          </>
        )}
      </div>
      {hasLoanEvent && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1.5">Échéances prêts</div>
          <div className="space-y-1">
            {item.loan_events.map((ev, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs text-red-200">
                <span className="truncate">{ev.lender}</span>
                <span className="font-mono font-bold shrink-0">−{fmt(ev.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasVacancy && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1.5">Loyers non perçus</div>
          <div className="space-y-1">
            {item.vacancy_impacts.map((v, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs text-red-200">
                <span className="truncate">{v.tenant_name} <span className="text-red-400/70">({v.reason})</span></span>
                <span className="font-mono font-bold shrink-0">−{fmt(v.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MiniSparkline = ({ history }) => {
  if (!history || history.length === 0) return <span className="text-slate-500 text-xs">—</span>;
  const balances = history.map((h) => h.balance);
  const max = Math.max(...balances, 1);
  const min = Math.min(...balances, 0);
  const range = max - min || 1;
  return (
    <div className="flex items-end gap-0.5 h-8 w-32">
      {balances.map((b, i) => {
        const h = ((b - min) / range) * 100;
        const isNeg = b < 0;
        return (
          <div
            key={i}
            className={`flex-1 rounded-sm ${isNeg ? "bg-red-400/60" : "bg-amber-400/70"}`}
            style={{ height: `${Math.max(4, h)}%` }}
            title={`${history[i].month}: ${b.toLocaleString("fr-FR")} €`}
          />
        );
      })}
    </div>
  );
};

const BreakdownRow = ({ label, value, tone = "slate" }) => {
  const tones = { emerald: "text-emerald-300", red: "text-red-300", slate: "text-slate-300" };
  return (
    <div className="flex justify-between items-center py-1.5 text-sm">
      <span className="text-slate-300">{label}</span>
      <span className={`font-mono font-bold ${tones[tone]}`}>{fmtEUR(value)}</span>
    </div>
  );
};
